import { Directive, ElementRef, EventEmitter, HostBinding, Inject, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { App } from '@df/core/app';
import { ElementHeightService } from '@df/dom/element-height/element-height.service';
import detectPassiveEvents from '@df/dom/fn/detect-passive-events';
import { DOCUMENT, WINDOW } from '@df/dom/globals';
import { ProcessorService } from '@df/ng/processor.service';

export enum TStickyPanelMode {
    relative = 'relative',
    stickyTop = 'stickyTop',
    stickyBottom = 'stickyBottom'
}

export interface IStickyPanelEvent {
    oldMode: TStickyPanelMode;
    newMode: TStickyPanelMode;
}

let stickyProp: string;

if (typeof CSS !== 'undefined' && CSS.supports) {
    if (CSS.supports('position', 'sticky')) {
        stickyProp = 'sticky';
    } else if (CSS.supports('position', '-webkit-sticky')) {
        stickyProp = '-webkit-sticky';
    }
}

/**
 * Sticky panel directive
 *
 * @example
 * ```html
 * <div [stickyPanel]></div>
 * ```
 */
@Directive({
    selector: '[stickyPanel]',
    standalone: true
})
export class LocalStickyPanelDirective implements OnInit, OnChanges {
    /**
     * Optionally enable/disable sticky panel
     */
    @Input()
    stickyPanel = true;

    @Input()
    stickyPanelOffsetTop: number | string | undefined = 0;

    @Input()
    stickyPanelOffsetBottom = 0;

    @Input()
    stickyPanelBottom?: boolean;

    @Input()
    stickyPanelOff?: boolean;

    @Output()
    stickyPanelOnChangeMode = new EventEmitter<IStickyPanelEvent>();

    @HostBinding('class')
    getRootClassName = 'sticky-panel';

    private node: HTMLElement;
    private mode?: TStickyPanelMode;
    private scrollPanel: HTMLElement | Window | null;
    private offset = 0;
    private latestScrollY = 0;
    private nodeHeight = 0;
    private scrollPaneOffset = 0;
    private naturalTop = 0;
    private viewportHeight = 0;
    private parentHeight = 0;
    private unsubscribes: (() => void)[] = [];

    /**
     * Says if it is enabled alread (used to check if we should make a change on toggling stickyPanel state)
     */
    private stickyPanelState = false;
    /**
     * If init was callsed
     */
    private initiated = false;

    constructor(@Inject(ElementRef) private elementRef: ElementRef<HTMLElement>, @Inject(App) private app: App) {}

    ngOnInit(): void {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.updateState();
        this.initiated = true;
    }

    ngOnChanges(changes) {
        if (this.initiated) {
            if (changes.stickyPanel) {
                this.updateState();
            }
        }
    }

    private updateState() {
        if (!this.app.isPlatformBrowser) {
            return;
        }

        this.node = this.elementRef.nativeElement as HTMLElement;
        const state = !!(this.node && this.stickyPanel);

        if (this.stickyPanelState !== state) {
            this.stickyPanelState = state;
            if (state) {
                this.scrollPanel = this.getScrollParent(this.node);
                this.latestScrollY = this.scrollPanel === window ? window.scrollY : (<HTMLElement>this.scrollPanel).scrollTop;

                this.addListener(this.scrollPanel, 'scroll', () => this.handleScroll(), { passive: detectPassiveEvents.hasSupport });
                this.addListener(this.scrollPanel, 'mousewheel', () => this.handleScroll(), { passive: detectPassiveEvents.hasSupport });

                if (this.scrollPanel === window) {
                    this.addListener(window, 'resize', () => this.handleWindowResize());
                    this.handleWindowResize();
                } else {
                    this.addResizeObserver(<HTMLElement>this.scrollPanel, this.handleScrollPaneResize);
                    this.handleScrollPaneResize();
                }

                this.addResizeObserver(<HTMLElement>this.node.parentNode, () => this.handleParentNodeResize());
                this.handleParentNodeResize();

                this.addResizeObserver(this.node, () => this.handleNodeResize());
                this.handleNodeResize(true);

                this.initial();
            } else {
                this.unsubscribes.forEach(fn => fn());
                this.unsubscribes = [];
                this.scrollPanel = null;
                if (this.node) {
                    this.node.style.position = 'static';
                }
            }
        }
    }

    readonly window = inject(WINDOW);
    readonly document = inject(DOCUMENT);

    // with this logic offset is automatically calculated basing on element height directive
    get topOffset() {
        const elementHeight = ProcessorService.injector.get<ElementHeightService>(ElementHeightService);

        return typeof this.stickyPanelOffsetTop === 'undefined'
            ? elementHeight.getTopOffset()
            : typeof this.stickyPanelOffsetTop === 'string'
            ? elementHeight.getState(this.stickyPanelOffsetTop).value
            : this.stickyPanelOffsetTop;
    }

    private initial() {
        if (this.stickyPanelBottom) {
            if (this.mode !== TStickyPanelMode.stickyBottom) {
                this.changeMode(TStickyPanelMode.stickyBottom);
            }
        } else {
            if (this.mode !== TStickyPanelMode.stickyTop) {
                this.changeMode(TStickyPanelMode.stickyTop);
            }
        }
    }

    private addListener(element: HTMLElement | Window, event: string, handler: any, passive?: { passive: boolean }) {
        element.addEventListener(event, handler, passive);

        this.unsubscribes.push(() => element.removeEventListener(event, handler));
    }

    private addResizeObserver(node: HTMLElement, handler: ResizeObserverCallback) {
        const ro = new ResizeObserver(handler);

        if (node && node instanceof HTMLElement) {
            ro.observe(node);
        }

        this.unsubscribes.push(() => ro.disconnect());
    }

    private changeMode(newMode: TStickyPanelMode) {
        if (this.mode !== newMode && this.stickyPanelOnChangeMode) {
            this.stickyPanelOnChangeMode.next({
                oldMode: this.mode,
                newMode
            });
        }

        this.mode = newMode;

        if (newMode === TStickyPanelMode.relative) {
            this.node.style.position = 'relative';

            if (this.stickyPanelBottom) {
                const nextBottom = Math.max(0, this.parentHeight - this.nodeHeight - this.offset);
                this.node.style.bottom = `${nextBottom}px`;
            } else {
                this.node.style.top = `${this.offset}px`;
            }
        } else {
            this.node.style.position = stickyProp;

            if (newMode === TStickyPanelMode.stickyBottom) {
                if (this.stickyPanelBottom) {
                    this.node.style.bottom = `${this.stickyPanelOffsetBottom}px`;
                } else {
                    this.node.style.top = `${this.viewportHeight - this.nodeHeight - this.stickyPanelOffsetBottom}px`;
                }
            } else {
                // stickyTop
                if (this.stickyPanelBottom) {
                    this.node.style.bottom = `${this.viewportHeight - this.nodeHeight - this.stickyPanelOffsetBottom}px`;
                } else {
                    this.node.style.top = `${this.topOffset}px`;
                }
            }
        }

        this.offset = this.getCurrentOffset();
    }

    private getCurrentOffset(): number {
        if (this.mode === TStickyPanelMode.relative) {
            return this.offset;
        }

        if (this.mode === TStickyPanelMode.stickyTop) {
            return Math.max(0, this.scrollPaneOffset + this.latestScrollY - this.naturalTop + this.topOffset);
        }

        if (this.mode === TStickyPanelMode.stickyBottom) {
            return Math.max(
                0,
                this.scrollPaneOffset +
                    this.latestScrollY +
                    this.viewportHeight -
                    (this.naturalTop + this.nodeHeight + this.stickyPanelOffsetBottom)
            );
        }

        return 0;
    }

    private changeToStickyBottomIfBoxTooLow(scrollY: number) {
        if (
            scrollY + this.scrollPaneOffset + this.viewportHeight >=
            this.naturalTop + this.nodeHeight + this.offset + this.stickyPanelOffsetBottom
        ) {
            this.changeMode(TStickyPanelMode.stickyBottom);
        }
    }

    protected handleWindowResize() {
        this.viewportHeight = this.window.innerHeight;
        this.scrollPaneOffset = 0;

        this.handleScroll();
    }

    private handleScrollPaneResize() {
        if (!this.stickyPanel) {
            return;
        }

        const scrollPane = <HTMLElement>this.scrollPanel;

        this.viewportHeight = scrollPane.offsetHeight;

        if (this.viewportHeight === 0) {
            this.app.logger.warn('stickyPanel scroll pane has a height of 0. This seems odd. Please check this node:', this.scrollPanel);
        }

        // Only applicable if scrollPane is an offsetParent
        if (scrollPane && scrollPane.firstChild && (<HTMLElement>scrollPane.firstChild).offsetParent === this.scrollPanel) {
            this.scrollPaneOffset = scrollPane.getBoundingClientRect().top;
        } else {
            this.scrollPaneOffset = 0;
        }

        this.handleScroll();
    }

    private handleParentNodeResize() {
        const parentNode = this.node.parentNode as HTMLElement;
        const computedParentStyle = this.window.getComputedStyle(parentNode, null);
        const parentPaddingTop = parseInt(computedParentStyle.getPropertyValue('padding-top'), 10);
        const parentPaddingBottom = parseInt(computedParentStyle.getPropertyValue('padding-bottom'), 10);
        const verticalParentPadding = parentPaddingTop + parentPaddingBottom;
        this.naturalTop = this.getOffsetTill(parentNode, <HTMLElement>this.scrollPanel) + parentPaddingTop + this.scrollPaneOffset;
        const oldParentHeight = this.parentHeight;
        this.parentHeight = parentNode.getBoundingClientRect().height - verticalParentPadding;

        if (this.mode === TStickyPanelMode.relative) {
            if (this.stickyPanelBottom) {
                this.changeMode(TStickyPanelMode.relative);
            } else {
                // If parent height decreased...
                if (oldParentHeight > this.parentHeight) {
                    this.changeToStickyBottomIfBoxTooLow(this.latestScrollY);
                }
            }
        }

        if (oldParentHeight !== this.parentHeight && this.mode === TStickyPanelMode.relative) {
            this.latestScrollY = Number.POSITIVE_INFINITY;
            this.handleScroll();
        }
    }

    private handleNodeResize(initial?: boolean) {
        const prevHeight = this.nodeHeight;

        this.nodeHeight = this.node.getBoundingClientRect().height;

        if (!initial && prevHeight !== this.nodeHeight) {
            if (this.nodeHeight + this.topOffset + this.stickyPanelOffsetBottom <= this.viewportHeight) {
                // Just make it sticky if node smaller than viewport
                this.mode = undefined;
                this.initial();
                return;
            } else {
                const diff = prevHeight - this.nodeHeight;
                const lowestPossible = this.parentHeight - this.nodeHeight;

                const nextOffset = Math.min(lowestPossible, this.getCurrentOffset() + (this.stickyPanelBottom ? diff : 0));
                this.offset = Math.max(0, nextOffset);

                if (!this.stickyPanelBottom || this.mode !== TStickyPanelMode.stickyBottom) {
                    this.changeMode(TStickyPanelMode.relative);
                }
            }
        }
    }

    private handleScroll() {
        const scrollY = this.scrollPanel === this.window ? this.window.scrollY : (<HTMLElement>this.scrollPanel).scrollTop;

        if (scrollY === this.latestScrollY || !this.stickyPanel) {
            return;
        }

        if (this.nodeHeight + this.topOffset + this.stickyPanelOffsetBottom <= this.viewportHeight) {
            // Just make it sticky if node smaller than viewport
            this.initial();
            this.latestScrollY = scrollY;

            return;
        }

        const scrollDelta = scrollY - this.latestScrollY;
        this.offset = this.getCurrentOffset();

        if (scrollDelta > 0) {
            // scroll down
            if (this.mode === TStickyPanelMode.stickyTop) {
                if (scrollY + this.scrollPaneOffset + this.topOffset > this.naturalTop) {
                    if (
                        scrollY + this.scrollPaneOffset + this.viewportHeight <=
                        this.naturalTop + this.nodeHeight + this.offset + this.stickyPanelOffsetBottom
                    ) {
                        this.changeMode(TStickyPanelMode.relative);
                    } else {
                        this.changeMode(TStickyPanelMode.stickyBottom);
                    }
                }
            } else if (this.mode === TStickyPanelMode.relative) {
                this.changeToStickyBottomIfBoxTooLow(scrollY);
            }
        } else {
            // scroll up
            if (this.mode === TStickyPanelMode.stickyBottom) {
                if (
                    this.scrollPaneOffset + scrollY + this.viewportHeight <
                    this.naturalTop + this.parentHeight + this.stickyPanelOffsetBottom
                ) {
                    if (this.scrollPaneOffset + scrollY + this.topOffset >= this.naturalTop + this.offset) {
                        this.changeMode(TStickyPanelMode.relative);
                    } else {
                        this.changeMode(TStickyPanelMode.stickyTop);
                    }
                }
            } else if (this.mode === TStickyPanelMode.relative) {
                if (this.scrollPaneOffset + scrollY + this.topOffset < this.naturalTop + this.offset) {
                    this.changeMode(TStickyPanelMode.stickyTop);
                }
            }
        }

        this.latestScrollY = scrollY;
    }

    private getScrollParent(node: HTMLElement): HTMLElement | Window | null {
        let parent: HTMLElement | null = node;

        while ((parent = parent.parentElement)) {
            const overflowYVal = this.window.getComputedStyle(parent, null).getPropertyValue('overflow-y');

            if (parent === this.document.body) {
                return this.window as Window;
            }

            if ((overflowYVal === 'auto' || overflowYVal === 'scroll') && !!parent.offsetHeight) {
                return parent;
            }
        }

        return this.window as Window;
    }

    private getOffsetTill(node: HTMLElement, target: HTMLElement | null): number {
        let current: HTMLElement | null = node;
        let offset = 0;

        // If target is not an offsetParent itself, subtract its offsetTop and set correct target
        if (target && target.firstChild && (<HTMLElement>target.firstChild).offsetParent !== target) {
            offset += node.offsetTop - target.offsetTop;

            target = <HTMLElement>node.offsetParent;

            offset += -node.offsetTop;
        }

        do {
            offset += current.offsetTop;
            current = <HTMLElement>current.offsetParent;
        } while (current && current !== target);

        return offset;
    }
}
